<template>
    <form-generator
        v-model="reactiveValue"
        :schema="formSchema"
        value-class=""
    ></form-generator>
</template>

<script>
import HocFormGenerator from '@/components/FormGenerator/HocFormGenerator';
import FormLayout from './SpaceFormLayout';
import { mapState } from 'vuex';
import { pick, sortBy } from 'lodash';
import { checkAlphaNumericOrDash } from '@/common/validationRules';

const FormGenerator = HocFormGenerator(FormLayout);

export default {
    name: 'SpaceEditForm',
    components: {
        'form-generator': FormGenerator
    },
    props: {
        value: {
            type: Object,
            required: false,
            default: () => {}
        },
        hasServiceAccount: {
            type: Boolean,
            default: false
        },
        visibility: {
            type: String,
            default: 'PUBLIC'
        },
        selectedVenueInfo: {
            type: Object,
            required: false,
            default: () => {}
        }
    },
    computed: {
        ...mapState('channelMaps', ['channelMaps']),
        // ...mapState('tags', { allTags: 'spaceTags' }),
        ...mapState('tags', ['spaceTags']),
        reactiveValue: {
            get() {
                return this.value;
            },
            set(newValue) {
                //Convert space attributes to uppercase before emmitting the input event
                newValue.name = newValue.name && newValue.name.toUpperCase();
                newValue.building =
                    newValue.building && newValue.building.toUpperCase();
                newValue.wing = newValue.wing && newValue.wing.toUpperCase();
                newValue.floor = newValue.floor && newValue.floor.toUpperCase();
                newValue.room = newValue.room && newValue.room.toUpperCase();
                this.$emit('input', newValue);
            }
        },
        visibilityOptions() {
            let items = [];
            if (this.selectedVenueInfo.defaultPublicChannelMapId) {
                //Check if in venueChannelMap is Public
                items.push({ text: 'Public', value: 'PUBLIC' });
            }
            if (this.selectedVenueInfo.defaultPrivateChannelMapId) {
                items.push({ text: 'Private', value: 'PRIVATE' });
            }
            return items;
        },
        visibleChannelMaps() {
            // filter channelMaps to match the current space visibility
            if (this.channelMaps && this.channelMaps.length) {
                return this.channelMaps.filter(item => {
                    return item.visibility === this.visibility;
                });
            }
            return [];
        },
        tagsOptions() {
            return sortBy(
                this.spaceTags.map(item => {
                    return pick(item, ['id', 'fullId', 'name', 'type']);
                }),
                ['name']
            );
        },
        channelMapField() {
            if (this.hasServiceAccount) {
                return {
                    name: 'channelMapId',
                    hideLabel: true,
                    component: {
                        type: 'v-select',
                        props: {
                            label: 'Channel Lineup',
                            dense: true,
                            required: true,
                            disabled: !this.$can('edit', 'ServiceAccount'),
                            itemText: item =>
                                item.displayName || item.locationName,
                            itemValue: 'id',
                            items: this.visibleChannelMaps
                        }
                    }
                };
            }
            return {
                name: 'channelMapName',
                hideLabel: true,
                valueClass: 'text-subtitle-1 font-weight-bold',
                component: {
                    type: 'v-text-field',
                    props: {
                        label: 'Channel Lineup',
                        dense: true,
                        disabled: true
                    }
                }
            };
        },
        formSchema() {
            return [
                {
                    name: 'name',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Room Name *',
                            dense: true,
                            required: true,
                            maxlength: 300
                        }
                    }
                },
                {
                    name: 'visibility',
                    hideLabel: true,
                    component: {
                        type: 'v-select',
                        props: {
                            label: 'Visibility *',
                            dense: true,
                            required: true,
                            items: this.visibilityOptions
                        }
                    }
                },
                {
                    name: 'building',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Building',
                            dense: true,
                            maxlength: 350,
                            disabled: this.hasServiceAccount,
                            rules: [v => checkAlphaNumericOrDash(v, 'Building')]
                        }
                    }
                },
                {
                    name: 'wing',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Wing',
                            dense: true,
                            maxlength: 2000,
                            disabled: this.hasServiceAccount,
                            rules: [v => checkAlphaNumericOrDash(v, 'Wing')]
                        }
                    }
                },
                {
                    name: 'floor',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Floor *',
                            dense: true,
                            required: true,
                            maxlength: 2000,
                            disabled: this.hasServiceAccount,
                            rules: [v => checkAlphaNumericOrDash(v, 'Floor')]
                        }
                    }
                },
                {
                    name: 'room',
                    hideLabel: true,
                    component: {
                        type: 'v-text-field',
                        props: {
                            label: 'Room Number *',
                            dense: true,
                            required: true,
                            maxlength: 1000,
                            disabled: this.hasServiceAccount,
                            rules: [
                                v => checkAlphaNumericOrDash(v, 'Room Number')
                            ]
                        }
                    }
                },
                this.channelMapField,
                {
                    name: 'tags',
                    hideLabel: true,
                    component: {
                        type: 'v-autocomplete',
                        props: {
                            label: 'Tags',
                            dense: true,
                            multiple: true,
                            smallChips: true,
                            deletableChips: true,
                            hideSelected: true,
                            itemText: 'name',
                            returnObject: true,
                            items: this.tagsOptions
                        }
                    }
                }
            ];
        }
    }
};
</script>

<style scoped lang="scss"></style>
